<!-- PlayButtonOverlay.vue -->
<template>
    <div
      v-if="visible"
      class="play-overlay"
      :class="{ 'with-thumbnail': thumbnail }"
      @click="startPlayback"
    >
      <div class="play-overlay-backdrop"></div>

      <div class="play-overlay-content">
        <!-- Optional thumbnail image background -->
        <img
          v-if="thumbnail"
          :src="thumbnail"
          alt="Conversation thumbnail"
          class="overlay-thumbnail"
        />

        <div class="play-button-container">
          <button class="play-button" aria-label="Play conversation">
            <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          </button>

          <div class="play-info">
            <h2 v-if="title" class="play-title">{{ title }}</h2>
            <p v-if="description" class="play-description">{{ description }}</p>
            <span class="play-prompt">Click to play conversation</span>
          </div>
        </div>
      </div>
    </div>
  </template>

  <script>
  export default {
    name: 'PlayButtonOverlay',
    props: {
      visible: {
        type: Boolean,
        default: true
      },
      title: {
        type: String,
        default: ''
      },
      description: {
        type: String,
        default: ''
      },
      thumbnail: {
        type: String,
        default: null
      },
      sharedId: {
        type: String,
        default: null
      }
    },
    methods: {
      startPlayback() {
        this.$emit('play');

        // Store in local storage that this shared conversation has been played
        if (this.sharedId) {
          localStorage.setItem(`played_conversation_${this.sharedId}`, 'true');
        }
      }
    }
  }
  </script>

  <style scoped>
  .play-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    overflow: hidden;
    border-radius: 0.5rem;
  }

  .play-overlay-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.65);
    backdrop-filter: blur(5px);
    z-index: 1001;
  }

  .play-overlay-content {
    position: relative;
    z-index: 1002;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  .overlay-thumbnail {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.6;
  }

  .play-button-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2.5rem;
    border-radius: 1rem;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(10px);
    transition: transform 0.3s ease;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }

  .play-overlay:hover .play-button-container {
    transform: scale(1.05);
  }

  .play-button {
    width: 110px;
    height: 110px;
    border-radius: 50%;
    background: linear-gradient(135deg, #4299e1, #3182ce);
    border: 4px solid rgba(255, 255, 255, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    cursor: pointer;
    margin-bottom: 1.5rem;
    box-shadow: 0 8px 25px rgba(66, 153, 225, 0.6);
    transition: all 0.3s ease;
    position: relative;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(66, 153, 225, 0.8);
    }
    70% {
      box-shadow: 0 0 0 20px rgba(66, 153, 225, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(66, 153, 225, 0);
    }
  }

  .play-overlay:hover .play-button {
    background: linear-gradient(135deg, #3182ce, #2b6cb0);
    transform: scale(1.15);
    animation-play-state: paused;
    border: 4px solid rgba(255, 255, 255, 0.8);
  }

  .play-button svg {
    margin-left: 8px; /* Slightly offset to center the triangle visually */
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  }

  .play-info {
    text-align: center;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  .play-title {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .play-description {
    font-size: 1.1rem;
    margin-bottom: 1rem;
    opacity: 0.9;
  }

  .play-prompt {
    font-size: 1rem;
    opacity: 0.9;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  /* Dark theme adjustments */
  .theme--dark .play-button {
    background: linear-gradient(135deg, #3182ce, #2b6cb0);
  }

  .theme--dark .play-overlay:hover .play-button {
    background: linear-gradient(135deg, #2b6cb0, #1a4a7a);
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .play-button {
      width: 90px;
      height: 90px;
    }

    .play-button svg {
      width: 45px;
      height: 45px;
    }

    .play-title {
      font-size: 1.5rem;
    }

    .play-description {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    .play-button-container {
      padding: 1.75rem;
    }

    .play-button {
      width: 75px;
      height: 75px;
      margin-bottom: 1rem;
      border-width: 3px;
    }

    .play-button svg {
      width: 40px;
      height: 40px;
    }

    .play-title {
      font-size: 1.25rem;
    }

    .play-description {
      font-size: 0.9rem;
      margin-bottom: 0.75rem;
    }

    .play-prompt {
      font-size: 0.85rem;
    }
  }
  </style>
